import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Paper,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Collapse,
  Grid,
  Switch,
  FormControlLabel,
} from "@mui/material";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import PrintIcon from "@mui/icons-material/Print";
import {
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Download as DownloadIcon,
  Print as PrintIconMui,
  ColorLens as ColorLensIcon,
  CropRotate as CropRotateIcon
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";

export default function CharacterCertificate({ receiptData, school, setTemID }) {
  const currentDate = new Date().toLocaleDateString("en-GB");
  const students = [{ ...receiptData?.data[0] }];

  // Customization controls
  const [themeColor, setThemeColor] = useState("#1976d2");
  const [isLandscape, setIsLandscape] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [borderStyle, setBorderStyle] = useState("solid"); // solid, dashed, dotted, double

  const toCamelCase = (str) => {
    if (!str) return "";
    return str
      ?.toLowerCase()
      .replace(/[^a-zA-Z0-9 ]/g, '')
      .split(' ')
      .map((word, index) =>
        index === 0
          ? word.charAt(0).toUpperCase() + word.slice(1)
          : word.charAt(0).toUpperCase() + word.slice(1)
      )
      .join(' ');
  };

  const receiptId = `character`;

  useEffect(() => {
    if (setTemID) {
      setTemID(receiptId);
    }
  }, [receiptData, setTemID, receiptId]);

  // Toggle landscape mode
  const toggleLandscape = () => {
    setIsLandscape(!isLandscape);
  };

  const borderColor = themeColor;
  const headerBg = alpha(themeColor, 0.14);
  
  // Get paper size based on orientation
  const getPaperSize = () => {
    if (isLandscape) {
      return { width: "1100px", minHeight: "500px" };
    }
    return { width: "750px", minHeight: "400px" };
  };

  const paperSize = getPaperSize();

  return (
    <Box
      sx={{
        p: 3,
        display: "flex",
        justifyContent: "center",
        backgroundColor: "#f3f6fc",
        minHeight: "40vh",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {/* Controls Panel */}
      <Paper
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "center",
          justifyContent: "center",
          maxWidth: "900px",
          margin: "auto",
          mb: 2,
          p: 1.5,
          "@media print": { display: "none" },
        }}
        elevation={2}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="body2" fontWeight={600} fontSize="0.8rem" component="span">
            <ColorLensIcon fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5 }} />
            Color:
          </Typography>
          <input
            type="color"
            value={themeColor}
            onChange={(e) => setThemeColor(e.target.value)}
            style={{
              width: 32,
              height: 28,
              padding: 0,
              border: "1px solid #ccc",
              borderRadius: 4,
              cursor: "pointer",
            }}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" fontWeight={600} fontSize="0.8rem" component="span">
            Border:
          </Typography>
          <select
            value={borderStyle}
            onChange={(e) => setBorderStyle(e.target.value)}
            style={{
              padding: "4px 8px",
              borderRadius: 4,
              border: "1px solid #ccc",
              fontSize: "0.75rem",
              cursor: "pointer",
            }}
          >
            <option value="solid">Solid</option>
            <option value="dashed">Dashed</option>
            <option value="dotted">Dotted</option>
            <option value="double">Double</option>
          </select>
        </Box>

        <Button
          size="small"
          variant={isLandscape ? "contained" : "outlined"}
          startIcon={<CropRotateIcon />}
          onClick={toggleLandscape}
          sx={{ fontSize: "0.75rem", textTransform: "none" }}
        >
          {isLandscape ? "Landscape" : "Portrait"}
        </Button>

        <Button
          size="small"
          variant="outlined"
          startIcon={<SettingsIcon />}
          onClick={() => setShowSettings(!showSettings)}
          sx={{ fontSize: "0.75rem", textTransform: "none" }}
        >
          Customize
        </Button>

      </Paper>

      {/* Settings Panel */}
      <Collapse in={showSettings}>
        <Paper
          sx={{
            maxWidth: "900px",
            margin: "auto",
            mb: 2,
            p: 2,
            bgcolor: '#f8f9fa',
            "@media print": { display: "none" },
          }}
        >
          <Typography variant="subtitle2" fontWeight="600" gutterBottom>
            Certificate Customization
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isLandscape}
                    onChange={toggleLandscape}
                    size="small"
                    color="primary"
                  />
                }
                label={
                  <Typography variant="caption">Landscape Mode</Typography>
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="caption" fontWeight="500">Border Style:</Typography>
                <select
                  value={borderStyle}
                  onChange={(e) => setBorderStyle(e.target.value)}
                  style={{
                    padding: "4px 8px",
                    borderRadius: 4,
                    border: "1px solid #ccc",
                    fontSize: "0.75rem",
                    cursor: "pointer",
                    flex: 1,
                  }}
                >
                  <option value="solid">Solid</option>
                  <option value="dashed">Dashed</option>
                  <option value="dotted">Dotted</option>
                  <option value="double">Double</option>
                </select>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {/* Certificate */}
      <Box id={receiptId}>
        <Paper
          elevation={6}
          sx={{
            width: paperSize.width,
            minHeight: paperSize.minHeight,
            p: 5,
            backgroundColor: "white",
            borderRadius: 2,
            position: "relative",
            fontFamily: "serif",
            border: `3px ${borderStyle} ${borderColor}`,
            boxShadow: `0 0 0 4px ${alpha(borderColor, 0.1)}`,
            transition: "all 0.3s ease",
            "@media print": {
              border: `3px ${borderStyle} ${borderColor}`,
              boxShadow: `0 0 0 4px ${alpha(borderColor, 0.1)}`,
            },
          }}
        >
          {/* Decorative Border Inner */}
          <Box
            sx={{
              position: "absolute",
              top: 12,
              left: 12,
              right: 12,
              bottom: 12,
              border: `1px solid ${alpha(borderColor, 0.3)}`,
              borderRadius: 1,
              pointerEvents: "none",
            }}
          />

          {/* Header */}
          <Box textAlign="center" mb={2}>
            <Box sx={{ display: "flex", justifyContent: "space-between", flexDirection: "column", alignItems: "center", mb: 1, mt: 0 }}>
              <img
                src={school?.logoUrl}
                alt="School Logo"
                style={{ width: isLandscape ? 70 : 80, height: isLandscape ? 60 : 70, marginTop: 10 }}
                onError={(e) => e.target.style.display = 'none'}
              />

              <Typography variant={isLandscape ? "h5" : "h6"} align="center" fontWeight="bold" sx={{ color: borderColor }}>
                {toCamelCase(school?.schoolName)}
              </Typography>
              <Typography variant="subtitle2" align="center" sx={{ fontSize: isLandscape ? "0.8rem" : "0.9rem" }}>
                {toCamelCase([school?.addressDTO?.addressLine1, school?.addressDTO?.city, school?.addressDTO?.pincode, school?.addressDTO?.state].filter(Boolean).join(", "))}
              </Typography>
            </Box>
            
            <Divider sx={{ my: 2, borderColor: borderColor }} />
            
            <Typography
              variant={isLandscape ? "h5" : "h6"}
              fontWeight="bold"
              textTransform="uppercase"
              sx={{ 
                color: borderColor,
                letterSpacing: 2,
                position: "relative",
                display: "inline-block",
                px: 3,
                py: 0.5,
                backgroundColor: alpha(borderColor, 0.05),
                borderRadius: 1,
              }}
            >
              Character Certificate
            </Typography>
          </Box>

          {/* Body Content */}
          {students.map((student, i) => {
            return (
              <Typography
                key={i}
                variant="body1"
                sx={{ 
                  mt: 4, 
                  textAlign: "justify", 
                  lineHeight: 2.2, 
                  fontSize: isLandscape ? "1.2rem" : "1.1rem",
                  color: "#333",
                  px: 2,
                }}
              >
                This is to certify that{" "}
                <strong style={{ color: borderColor }}>
                  {toCamelCase([student?.name, student?.last].filter(Boolean).join(" ")) || "____________"}
                </strong>, son/daughter of
                Mr./Ms. <strong style={{ color: borderColor }}>
                  {toCamelCase([student?.father, student?.last].filter(Boolean).join(" ")) || "____________"}
                </strong> is/was
                a student of{" "}
                <strong style={{ color: borderColor }}>
                  {toCamelCase(school?.schoolName) || "____________"}
                </strong> during the
                year <strong style={{ color: borderColor }}>
                  {student?.classRoom?.academicYear || "__________"}
                </strong>.{" "}
                His/Her character and conduct were{" "}
                <strong style={{ color: borderColor }}>
                  {student?.character || "Good"}
                </strong>{" "}
                during his/her stay in this school.
              </Typography>
            );
          })}

          {/* Date */}
          <Typography
            sx={{ mt: 4, mb: 8, fontSize: isLandscape ? "1.2rem" : "1.1rem" }}
          >
            Date: <strong style={{ color: borderColor }}>{currentDate}</strong>
          </Typography>

          {/* Signature Section */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mt: 6,
              textAlign: "center",
              px: 2,
            }}
          >
            <Box>
              <Typography variant="body1" sx={{ mb: 1, color: borderColor }}>
                ______________________
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>(Class Teacher)</Typography>
            </Box>

            <Box>
              <Typography variant="body1" sx={{ mb: 1, color: borderColor }}>
                ______________________
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>(Admission Incharge)</Typography>
            </Box>

            <Box>
              <Typography variant="body1" sx={{ mb: 1, color: borderColor }}>
                ______________________
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>(Head Master)</Typography>
              <Typography variant="body2" sx={{ color: borderColor }}>(Seal)</Typography>
            </Box>
          </Box>

          {/* Footer Decorative Line */}
          <Divider 
            sx={{ 
              mt: 4, 
              borderColor: alpha(borderColor, 0.3),
              borderWidth: 2,
            }} 
          />
          
          <Typography 
            variant="caption" 
            align="center" 
            display="block" 
            sx={{ mt: 1, color: alpha(borderColor, 0.5) }}
          >
            This is a computer generated certificate. No signature required.
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
}